import type { Announcements, UniqueIdentifier } from '@dnd-kit/core';

export interface SortableLayersAnnouncementOptions {
  getName: (id: UniqueIdentifier) => string;
  /** 1-based position of `id` in the CURRENT (pre-drop) order, or `null` if not found. */
  getPosition: (id: UniqueIdentifier) => number | null;
  /** Optional: how a landing slot reads aloud (default `position <n>`), e.g. `Bill to, position <n>`. */
  describePosition?: (id: UniqueIdentifier, position: number) => string;
  /** Optional: the reason a move onto `overId` is refused; when set it is announced instead of the move. */
  getRejection?: (activeId: UniqueIdentifier, overId: UniqueIdentifier) => string | null;
}

/**
 * Pickup / over / drop / cancel announcements for a `SortableLayers` list, naming the row and its
 * position - `CLAUDE.md` Accessibility requires the `<DndContext>` `accessibility` prop wired
 * with these on every dnd-kit sortable list (see `CompositionList` for the prior art this mirrors).
 */
export function useSortableLayersAnnouncements({
  getName,
  getPosition,
  describePosition,
  getRejection
}: SortableLayersAnnouncementOptions): Announcements {
  const slot = (id: UniqueIdentifier, position: number) => describePosition?.(id, position) ?? `position ${position}`;

  return {
    onDragStart: ({ active }) => `Picked up ${getName(active.id)}.`,
    onDragOver: ({ active, over }) => {
      if (!over) return '';
      const rejection = getRejection?.(active.id, over.id);
      if (rejection) return rejection;
      const position = getPosition(over.id);
      return position ? `${getName(active.id)} moved to ${slot(over.id, position)}.` : '';
    },
    onDragEnd: ({ active, over }) => {
      if (!over) return `Reorder of ${getName(active.id)} cancelled.`;
      const rejection = getRejection?.(active.id, over.id);
      if (rejection) return rejection;
      const position = getPosition(over.id);
      return position ? `${getName(active.id)} dropped at ${slot(over.id, position)}.` : `${getName(active.id)} dropped.`;
    },
    onDragCancel: ({ active }) => `Reorder of ${getName(active.id)} cancelled.`
  };
}
